// @flow

import * as React from 'react';
import PropTypes from 'prop-types';
import { type ContextRouter } from 'react-router-dom';
import Cookies from 'js-cookie';

import Loader from './components/Loader';

import { defaultUserData } from './utils/data';
import { resetTableFilterSettings } from './utils/localstorage';
import withContext from './utils/withContext';

import {
  beginPinReset,
  getAccountInfo,
  getParkerStatus,
  logout,
} from './api/auth';

import type { AccountInfo } from './api/types';

type _ExtractReturn<B, _F: (...args: any[]) => B> = B;
type ExtractReturn<F> = _ExtractReturn<*, F>;
export type UserData = ExtractReturn<typeof defaultUserData>;

const isProfileComplete = (
  accountInfo: AccountInfo | null,
  userData: UserData | null
) => {
  if (userData === null) return false;
  if (accountInfo === null) return false;
  const fleetAccount = userData.fleetAccount;
  if (fleetAccount === null) return false;

  return !!(
    fleetAccount.name &&
    fleetAccount.contact_email &&
    accountInfo.address &&
    accountInfo.city &&
    accountInfo.stateabbreviation &&
    accountInfo.zipcode &&
    userData.firstname &&
    userData.emailaddress &&
    userData.phonenumber
  );
};

const isBillingComplete = (userData: UserData) => {
  if (userData === null) return false;
  const fleetAccount = userData.fleetAccount;
  const paymentMethods = userData.paymentmethods;
  if (fleetAccount === null) return false;
  if (paymentMethods === null) return false;

  const familyAccountZoneCash = paymentMethods.zonecash.find(
    zc => zc.id === fleetAccount.wallet_id
  );

  return !!(familyAccountZoneCash && familyAccountZoneCash.card);
};

type UserState = 'ANONYMOUS' | 'VERIFIED' | 'AUTHENTICATED' | 'UNKNOWN';
const currentUserState = (accountInfo, userData): UserState => {
  if (!userData || !accountInfo) {
    return 'UNKNOWN';
  }

  const hasVerifiedParkerCookie = !!Cookies.get('ppverifiedparker');
  const isLoggedIn = hasVerifiedParkerCookie && Cookies.get('ppsessiondata');
  if (isLoggedIn) {
    if (
      isProfileComplete(accountInfo, userData) &&
      isBillingComplete(userData)
    ) {
      return 'AUTHENTICATED';
    }
    return 'VERIFIED';
  }

  return 'ANONYMOUS';
};

type State = {
  userState: UserState,
  accountInfo: AccountInfo | null,
  userData: UserData | null,
  loading: boolean,
  bypassId: number | null,
};

type Props = {
  ...ContextRouter,
  children: (data: { userState: UserState }) => React.Node,
};

type AuthChangePayload =
  | { type: 'LOGIN' }
  | { type: 'LOGOUT' }
  | { type: 'SIGNED_UP' }
  | { type: 'WRONG_FLOW' }
  | { type: 'UNVERIFY' }
  | {
      type: 'VERIFY',
      payload: {
        email: string,
        accountInfo: AccountInfo,
        userData: UserData,
      },
    }
  | { type: 'BEGIN_PIN_RESET' }
  | { type: 'PIN_RESET' };

class UserProvider extends React.Component<Props, State> {
  state = {
    userState: currentUserState() === 'ANONYMOUS' ? 'ANONYMOUS' : 'UNKNOWN',
    loading: true,
    accountInfo: null,
    userData: null,
    bypassId: null,
  };

  componentWillMount() {
    if (this.state.userState !== 'ANONYMOUS') {
      this.loadAccountInfo();
    } else {
      this.setState({ loading: false });
    }
  }

  static childContextTypes = {
    onAuthChanged: PropTypes.func,
    loadAccountInfo: PropTypes.func,
    accountInfo: PropTypes.object,
    userData: PropTypes.object,
    userState: PropTypes.string,
  };

  getChildContext() {
    return {
      onAuthChanged: this.onAuthChanged,
      loadAccountInfo: this.loadAccountInfo,
      accountInfo: this.state.accountInfo,
      userData: this.state.userData,
      userState: this.state.userState,
    };
  }

  loadAccountInfo = (callback?: Function) => {
    Promise.all([getAccountInfo(), getParkerStatus()])
      .then(([accountInfoResp, parkerStatus]) => {
        const accountInfo = accountInfoResp.data;
        const userData = defaultUserData(parkerStatus.data);

        if (!isProfileComplete(accountInfo, userData)) {
          this.props.history.push('/signup/fleet', {
            email: accountInfo.emailaddress,
          });
        } else if (!isBillingComplete(userData)) {
          this.props.history.push('/signup/billing', {
            email: accountInfo.emailaddress,
          });
        }

        this.setState(
          {
            loading: false,
            accountInfo: accountInfo,
            userData: userData,
            userState: currentUserState(accountInfo, userData),
          },
          callback
        );
      })
      .catch(error => {
        if (error.status === 400) {
          this.setState({ loading: false, userState: 'ANONYMOUS' });
        }
      });
  };

  startPinReset = () => {
    Promise.all([beginPinReset(), getAccountInfo(), getParkerStatus()])
      .then(([beginPinResetResp, accountInfoResp, parkerStatus]) => {
        const pinResetRequirements = beginPinResetResp.data;
        const accountInfo = accountInfoResp.data;
        const userData = defaultUserData(parkerStatus.data);

        const callback = () => {
          if (!isBillingComplete(userData) || pinResetRequirements.bypassid) {
            this.props.history.push('/login/reset-pin', {
              email: accountInfo.emailaddress,
              bypassId: pinResetRequirements.bypassid,
            });
          } else {
            const cardTail = !!pinResetRequirements.cardDetails
              ? pinResetRequirements.cardDetails.tail
              : '';
            this.props.history.push('/login/verify-billing', {
              newCardOk: pinResetRequirements.newCardOk,
              cardTail,
            });
          }
        };

        this.setState(
          {
            loading: false,
            accountInfo: accountInfo,
            userData: userData,
            userState: currentUserState(accountInfo, userData),
            bypassId: pinResetRequirements.bypassid,
          },
          callback
        );
      })
      .catch(error => {
        if (error.status === 400) {
          this.setState({ loading: false, userState: 'ANONYMOUS' });
        }
      });
  };

  onAuthChanged = (action: AuthChangePayload) => {
    switch (action.type) {
      case 'LOGIN':
        if (
          this.state.accountInfo &&
          this.state.userData &&
          this.state.userState === 'AUTHENTICATED'
        ) {
          this.setState({
            userState: currentUserState(),
          });
        } else {
          this.loadAccountInfo(() => {
            if (this.state.userState === 'AUTHENTICATED') {
              this.props.history.push('/members');
            }
          });
        }
        return;
      case 'LOGOUT':
        // TODO: Call logout api
        Promise.all([logout()])
          .then(([response]) => {
            if (response.status === 200) {
              this.props.history.push('/');
              resetTableFilterSettings();
              Cookies.remove('ppsessiondata');
              Cookies.remove('ppverifiedparker');
              this.setState({
                accountInfo: null,
                userData: null,
                userState: 'ANONYMOUS',
              });
            }
          })
          .catch(error => {
            console.log(error);
          });
        return;
      case 'SIGNED_UP':
        this.loadAccountInfo(() => {
          this.props.history.push('/members');
        });
        return;
      case 'BEGIN_PIN_RESET':
        this.startPinReset();
        return;
      case 'PIN_RESET':
        this.props.history.push('/login/reset-pin', {
          email: this.state.accountInfo && this.state.accountInfo.emailaddress,
          bypassId: this.state.bypassId,
        });
        return;
      default:
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            '<UserProvider /> onAuthChanged() received unexpected action type "%s"',
            action.type
          );
        }
    }
  };

  render() {
    if (this.state.loading || this.state.userState === 'UNKNOWN') {
      return <Loader center />;
    }
    return this.props.children({ userState: this.state.userState });
  }
}

export const connectToUser = withContext(UserProvider.childContextTypes);

export default UserProvider;



// WEBPACK FOOTER //
// ./src/UserProvider.js